'use client';

import { CategoryBadge, ContributeCta, DifficultyBadge, TablatureCard } from '@/components/features/tablatures/components';
import { ts } from '@/lib';
import { Badge } from '@/types/badges';
import { Avatar, LocalizedLink } from '@ui/primitives';
import { Section, Header, Grid, List, ListItem } from '@/_ui';

interface PublicProfileClientProps {
    profile: any;
    badges: Badge[];
    performances: any[];
    locale: string;
    dictionary: any;
}

export function PublicProfileClient({ profile, badges, performances, locale, dictionary }: PublicProfileClientProps) {
    const formatDate = (dateString: string) => {
        const date = new Date(dateString);
        return date.toLocaleDateString(locale, { year: 'numeric', month: 'long' });
    };

    const getRoleLabel = (role: number) => {
        const roles: { [key: number]: string } = {
            0: ts(dictionary, 'roles.role_0'),
            1: ts(dictionary, 'roles.role_1'),
            2: ts(dictionary, 'roles.role_2'),
            3: ts(dictionary, 'roles.role_3'),
            4: ts(dictionary, 'roles.role_4'),
            5: ts(dictionary, 'roles.role_5'),
            6: ts(dictionary, 'roles.role_6'),
        };
        return roles[role] || ts(dictionary, 'roles.role_2');
    };

    return (
        <Section container>
            {/* Header Section */}
            <div className="bg-white rounded-lg p-6 mb-6 border border-gray-200 shadow-sm">
                <div className="flex flex-col md:flex-row items-start md:items-center gap-6">
                    {/* Avatar */}
                    <div className="relative">
                        <Avatar
                            src={profile.imageUrl || '/images/avatar.png'}
                            alt={profile.displayName}
                            size='xxxl'
                            className="rounded-full object-cover"
                        />
                    </div>

                    {/* Info */}
                    <div className="flex-1">
                        <div className="flex items-center gap-3 mb-2">
                            <h1 className="text-3xl font-bold">{profile.displayName}</h1>
                            <span className="px-3 py-1 bg-gray-200 rounded-full text-sm font-medium">
                                {getRoleLabel(profile.role)}
                            </span>
                        </div>

                        {profile.signature && (
                            <p className="text-lg text-gray-600 mb-3">
                                {profile.signature}
                            </p>
                        )}

                        <div className="flex flex-wrap gap-4 text-sm text-gray-600">
                            {profile.location && (
                                <div className="flex items-center gap-1">
                                    <i className="fa-solid fa-location-dot text-xs" />
                                    <span>{profile.location}</span>
                                </div>
                            )}
                            <div className="flex items-center gap-1">
                                <i className="fa-solid fa-calendar text-xs" />
                                <span>
                                    {ts(dictionary, 'users.member_since')} {formatDate(profile.createdAt)}
                                </span>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Stats Section */}
            <Grid cols={4} gap={4} className="mb-8">
                <div className="bg-white rounded-lg p-6 text-center border border-gray-200 shadow-sm">
                    <i className="fa-solid fa-music text-4xl mx-auto mb-2 text-blue-500 block" />
                    <div className="text-2xl font-bold">{profile.tablatures?.length || 0}</div>
                    <div className="text-sm text-gray-600 ">
                        {ts(dictionary, 'users.tablatures_count', { count: profile.tablatures?.length || 0 })}
                    </div>
                </div>

                <div className="bg-white rounded-lg p-6 text-center border border-gray-200 shadow-sm">
                    <i className="fa-solid fa-comments text-4xl mx-auto mb-2 text-green-500 block" />
                    <div className="text-2xl font-bold">{profile.forumPostCount || 0}</div>
                    <div className="text-sm text-gray-600 ">
                        {ts(dictionary, 'forum.posts_count', { count: profile.forumPostCount || 0 })}
                    </div>
                </div>

                <div className="bg-white rounded-lg p-6 text-center border border-gray-200 shadow-sm">
                    <i className="fa-solid fa-crown text-4xl mx-auto mb-2 text-yellow-500 block" />
                    <div className="text-2xl font-bold">{badges.length}</div>
                    <div className="text-sm text-gray-600 ">
                        {ts(dictionary, 'badges.badges_count', { count: badges.length })}
                    </div>
                </div>

                <div className="bg-white rounded-lg p-6 text-center border border-gray-200 shadow-sm">
                    <i className="fa-solid fa-file-audio text-4xl mx-auto mb-2 text-purple-500 block" />
                    <div className="text-2xl font-bold">{performances.length}</div>
                    <div className="text-sm text-gray-600 ">
                        {ts(dictionary, 'audio.performances_count', { count: performances.length })}
                    </div>
                </div>
            </Grid>

            {/* Badges Section */}
            {badges && badges.length > 0 && (
                <Section>
                    <Header
                        title={ts(dictionary, 'badges.badges')}
                        icon={<i className="fas fa-medal" />}
                        size="lg"
                    />
                    <Grid cols={6} gap={4}>
                        {badges.map((badge: Badge) => (
                            <div
                                key={badge.id}
                                className="flex flex-col items-center p-3 bg-gray-50 rounded-lg border border-gray-100"
                                title={ts(dictionary, `badges.${badge.badge.code}.description`)}
                            >
                                <div className="text-4xl mb-2">{badge.badge.iconEmoji || badge.iconClass || '🏆'}</div>
                                <div className="text-xs text-center font-medium">{ts(dictionary, `badges.${badge.badge.code}.name`)}</div>
                            </div>
                        ))}
                    </Grid>
                </Section>
            )}

            {/* Tablatures Section */}
            <Section>
                <Header
                    title={ts(dictionary, 'users.public_tablatures')}
                    icon={<i className="fas fa-music" />}
                    size="lg"
                />

                {profile.tablatures && profile.tablatures.length > 0 ? (
                    <List variant="bordered">
                        {profile.tablatures.map((tab: any) => (
                            <ListItem key={tab.id} className="p-0">
                                <TablatureCard variant="full" dictionary={dictionary} locale={locale} tab={tab} />
                            </ListItem>
                        ))}
                    </List>
                ) : (
                    <p className="text-gray-600 text-center py-8">
                        {ts(dictionary, 'users.no_public_tablatures')}
                    </p>
                )}
            </Section>

            <ContributeCta locale={locale} dictionary={dictionary} source="profile" />
        </Section>
    );
}
